import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FieldData, LabelSheetConfig, LABEL_SHEET_PRESETS } from '@/types/label';
import { FileText, Upload, Trash2, FileSpreadsheet, Printer, Download, FileType } from 'lucide-react';
import { DataCarousel } from './DataCarousel';
import { SheetConfig } from './SheetConfig';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

interface BatchDataFormProps {
  fields: string[];
  fieldData: FieldData;
  onUpdateField: (field: string, value: string) => void;
  // CSV
  csvData: FieldData[];
  csvHeaders: string[];
  currentRowIndex: number;
  totalRows: number;
  onImportCsv: (file: File) => void;
  onClearCsv: () => void;
  onNextRow: () => void;
  onPrevRow: () => void;
  onGoToRow: (index: number) => void;
  // Text import (key:value)
  onImportText?: (file: File, opts: { mode: 'single' | 'multi'; separator?: string }) => Promise<unknown> | void;
  // Batch export
  sheetConfig: LabelSheetConfig;
  onSheetConfigChange: (config: LabelSheetConfig) => void;
  onExportBatch: () => void;
  // Template fields for sample CSV
  templateFields: string[];
  // Permission gates
  canBatchImport?: boolean;
  canUseDataCarousel?: boolean;
  canExportPdf?: boolean;
}

export const BatchDataForm: React.FC<BatchDataFormProps> = ({
  fields,
  fieldData,
  onUpdateField,
  csvData,
  csvHeaders,
  currentRowIndex,
  totalRows,
  onImportCsv,
  onClearCsv,
  onNextRow,
  onPrevRow,
  onGoToRow,
  onImportText,
  sheetConfig,
  onSheetConfigChange,
  onExportBatch,
  templateFields,
  canBatchImport = true,
  canUseDataCarousel = true,
  canExportPdf = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const txtInputRef = useRef<HTMLInputElement>(null);
  const [textMode, setTextMode] = useState<'single' | 'multi'>('single');
  const [textSeparator, setTextSeparator] = useState<string>('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImportCsv(file);
    }
    e.target.value = '';
  };

  const handleTextFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onImportText) {
      onImportText(file, { mode: textMode, separator: textSeparator });
    }
    e.target.value = '';
  };

  const downloadSampleTxt = () => {
    const fieldsToUse = templateFields.length > 0 ? templateFields : ['name', 'value'];
    const lines = [
      '# One field per line. Use "key: value" or "key = value".',
      '# Lines starting with # or // are ignored.',
      '# Separate multiple labels with a blank line.',
      '',
      ...fieldsToUse.map((f) => `${f}: `),
      '',
      ...fieldsToUse.map((f) => `${f}: `),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'label-template-data.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadSampleCsv = () => {
    if (templateFields.length === 0) return;
    
    // Add sep=, hint for Excel to recognize comma delimiter
    const sepHint = 'sep=,';
    const headers = templateFields.join(',');
    const emptyRows = Array(10).fill(templateFields.map(() => '').join(',')).join('\n');
    const csvContent = `${sepHint}\n${headers}\n${emptyRows}`;
    
    // Add BOM for Excel compatibility
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'label-template-data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };


  return (
    <Tabs defaultValue="manual" className="h-full flex flex-col">
      <div className="border-b border-border px-3 sm:px-4 pt-1.5 sm:pt-2">
        <TabsList className="h-8 sm:h-9">
          <TabsTrigger value="manual" className="text-[10px] sm:text-xs px-2 sm:px-3">Manual</TabsTrigger>
          {canBatchImport && (
            <TabsTrigger value="csv" className="text-[10px] sm:text-xs px-2 sm:px-3">CSV Import</TabsTrigger>
          )}
          {canBatchImport && onImportText && (
            <TabsTrigger value="text" className="text-[10px] sm:text-xs px-2 sm:px-3">Text Import</TabsTrigger>
          )}
          {canExportPdf && (
            <TabsTrigger value="batch" className="text-[10px] sm:text-xs px-2 sm:px-3">Batch</TabsTrigger>
          )}
        </TabsList>
      </div>

      <TabsContent value="manual" className="flex-1 m-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            <div className="flex items-center gap-1.5 sm:gap-2 pb-1.5 sm:pb-2 border-b border-border">
              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
              <h3 className="text-xs sm:text-sm font-medium">Data Fields</h3>
            </div>
            
            {fields.length === 0 ? (
              <div className="text-center text-muted-foreground py-6 sm:py-8">
                <FileText className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 opacity-50" />
                <p className="text-xs sm:text-sm">No dynamic fields detected</p>
                <p className="text-[10px] sm:text-xs mt-1">
                  Add {"{{fieldName}}"} to element content to create dynamic fields
                </p>
              </div>
            ) : (
              fields.map((field) => (
                <div key={field} className="space-y-1 sm:space-y-1.5">
                  <Label className="text-[10px] sm:text-xs font-medium">{field}</Label>
                  <Input
                    value={fieldData[field] || ''}
                    onChange={(e) => onUpdateField(field, e.target.value)}
                    placeholder={`Enter ${field}`}
                    className="h-8 sm:h-9 text-xs sm:text-sm"
                  />
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </TabsContent>

      <TabsContent value="csv" className="flex-1 m-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            <div className="flex items-center gap-1.5 sm:gap-2 pb-1.5 sm:pb-2 border-b border-border">
              <FileSpreadsheet className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
              <h3 className="text-xs sm:text-sm font-medium">CSV Data</h3>
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />

            {totalRows === 0 ? (
              <div className="text-center py-6 sm:py-8">
                <FileSpreadsheet className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-muted-foreground opacity-50" />
                <p className="text-xs sm:text-sm text-muted-foreground mb-3 sm:mb-4">
                  Import a CSV file to batch preview labels
                </p>
                <div className="flex flex-col gap-1.5 sm:gap-2">
                  <Button onClick={() => fileInputRef.current?.click()} className="gap-1.5 sm:gap-2 h-8 sm:h-9 text-xs sm:text-sm">
                    <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Import CSV
                  </Button>
                  {templateFields.length > 0 && (
                    <Button variant="outline" onClick={downloadSampleCsv} className="gap-1.5 sm:gap-2 h-8 sm:h-9 text-xs sm:text-sm">
                      <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      Download Sample
                    </Button>
                  )}
                </div>
                {templateFields.length > 0 && (
                  <p className="text-[9px] sm:text-xs text-muted-foreground mt-2 sm:mt-3">
                    Fields: {templateFields.join(', ')}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                {canUseDataCarousel && (
                  <DataCarousel
                    currentIndex={currentRowIndex}
                    totalRows={totalRows}
                    onGoToRow={onGoToRow}
                    onNext={onNextRow}
                    onPrev={onPrevRow}
                  />
                )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClearCsv}
                    className="gap-1 text-destructive h-7 sm:h-8 text-xs"
                  >
                    <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span className="hidden xs:inline">Clear</span>
                  </Button>
                </div>

                <div className="space-y-2 sm:space-y-3 pt-1.5 sm:pt-2">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    Showing row {currentRowIndex + 1} of {totalRows}
                  </p>
                  {csvHeaders.map((header) => (
                    <div key={header} className="space-y-1 sm:space-y-1.5">
                      <Label className="text-[10px] sm:text-xs font-medium">{header}</Label>
                      <Input
                        value={fieldData[header] || ''}
                        readOnly
                        className="h-8 sm:h-9 bg-muted text-xs sm:text-sm"
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </TabsContent>

      <TabsContent value="text" className="flex-1 m-0 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            <div className="flex items-center gap-1.5 sm:gap-2 pb-1.5 sm:pb-2 border-b border-border">
              <FileType className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary" />
              <h3 className="text-xs sm:text-sm font-medium">Text File Import</h3>
            </div>

            <input
              ref={txtInputRef}
              type="file"
              accept=".txt,text/plain"
              onChange={handleTextFileChange}
              className="hidden"
            />

            <div className="space-y-2">
              <Label className="text-[10px] sm:text-xs font-medium">Mode</Label>
              <div className="flex gap-1.5">
                <Button
                  type="button"
                  variant={textMode === 'single' ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1 h-8 text-xs"
                  onClick={() => setTextMode('single')}
                >
                  Single label
                </Button>
                <Button
                  type="button"
                  variant={textMode === 'multi' ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1 h-8 text-xs"
                  onClick={() => setTextMode('multi')}
                >
                  Multiple labels
                </Button>
              </div>
            </div>

            {textMode === 'multi' && (
              <div className="space-y-1.5">
                <Label className="text-[10px] sm:text-xs font-medium">
                  Record separator (optional)
                </Label>
                <Input
                  value={textSeparator}
                  onChange={(e) => setTextSeparator(e.target.value)}
                  placeholder="Blank line (default)"
                  className="h-8 text-xs"
                />
                <p className="text-[9px] sm:text-[10px] text-muted-foreground">
                  Leave empty to split records on blank lines, or enter a marker like
                  <code className="mx-1">---</code>.
                </p>
              </div>
            )}

            {totalRows === 0 ? (
              <div className="text-center py-4">
                <FileType className="w-10 h-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                <p className="text-xs sm:text-sm text-muted-foreground mb-3">
                  Upload a <code>.txt</code> file with <code>key: value</code> lines
                </p>
                <div className="flex flex-col gap-1.5 sm:gap-2">
                  <Button
                    onClick={() => txtInputRef.current?.click()}
                    className="gap-1.5 h-8 sm:h-9 text-xs sm:text-sm"
                  >
                    <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Import Text File
                  </Button>
                  <Button
                    variant="outline"
                    onClick={downloadSampleTxt}
                    className="gap-1.5 h-8 sm:h-9 text-xs sm:text-sm"
                  >
                    <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    Download Sample
                  </Button>
                </div>
                {templateFields.length > 0 && (
                  <p className="text-[9px] sm:text-xs text-muted-foreground mt-2 sm:mt-3">
                    Fields: {templateFields.join(', ')}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  {canUseDataCarousel && totalRows > 1 && (
                    <DataCarousel
                      currentIndex={currentRowIndex}
                      totalRows={totalRows}
                      onGoToRow={onGoToRow}
                      onNext={onNextRow}
                      onPrev={onPrevRow}
                    />
                  )}
                  <div className="flex gap-1 ml-auto">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => txtInputRef.current?.click()}
                      className="gap-1 h-7 sm:h-8 text-xs"
                    >
                      <Upload className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden xs:inline">Replace</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClearCsv}
                      className="gap-1 text-destructive h-7 sm:h-8 text-xs"
                    >
                      <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden xs:inline">Clear</span>
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 sm:space-y-3 pt-1.5 sm:pt-2">
                  <p className="text-[10px] sm:text-xs text-muted-foreground">
                    {totalRows === 1
                      ? `Parsed ${csvHeaders.length} field${csvHeaders.length === 1 ? '' : 's'}`
                      : `Showing record ${currentRowIndex + 1} of ${totalRows}`}
                  </p>
                  {csvHeaders.map((header) => (
                    <div key={header} className="space-y-1 sm:space-y-1.5">
                      <Label className="text-[10px] sm:text-xs font-medium">{header}</Label>
                      <Input
                        value={fieldData[header] || ''}
                        readOnly
                        className="h-8 sm:h-9 bg-muted text-xs sm:text-sm"
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </ScrollArea>
      </TabsContent>

      <TabsContent value="batch" className="flex-1 m-0 overflow-hidden">
        <ScrollArea className="h-full">
          <SheetConfig
            config={sheetConfig}
            onConfigChange={onSheetConfigChange}
          />
          
          <div className="p-3 sm:p-4 border-t border-border">
            <Button 
              className="w-full gap-1.5 sm:gap-2 h-8 sm:h-9 text-xs sm:text-sm" 
              onClick={onExportBatch}
              disabled={totalRows === 0}
            >
              <Printer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Export Batch ({totalRows} labels)
            </Button>
            {totalRows === 0 && (
              <p className="text-[9px] sm:text-xs text-muted-foreground text-center mt-1.5 sm:mt-2">
                Import CSV data first to export batch
              </p>
            )}
          </div>
        </ScrollArea>
      </TabsContent>
    </Tabs>
  );
};
